const SESSION_STORAGE_KEY = 'unblocked-content';

export class MrBlockedContent extends HTMLElement {
	static get observedAttributes(): Array<string> {
		return [
			'disabled',
			'data-state',
		];
	}

	// Attributes
	override setAttribute( attr: string, value: string ): void {
		if ( this.disabled ) {
			return;
		}

		if ( 'data-state' === attr ) {
			const states = [
				'blocked',
				'unblocked',
			];

			if ( -1 === states.indexOf( value ) ) {
				return;
			}

			if ( this.state === 'unblocked' ) {
				return;
			}

			super.setAttribute( 'data-state', value );

			return;
		}

		super.setAttribute( attr, value );
	}

	override removeAttribute( attr: string ): void {
		if ( this.disabled && 'disabled' !== attr ) {
			return;
		}

		super.removeAttribute( attr );
	}

	get disabled(): boolean {
		return this.hasAttribute( 'disabled' );
	}

	set disabled( value: boolean ) {
		if ( value ) {
			this.setAttribute( 'disabled', '' );
		} else {
			this.removeAttribute( 'disabled' );
		}
	}

	get state() :string {
		return this.getAttribute( 'data-state' ) || '';
	}

	set state( value: string ) {
		this.setAttribute( 'data-state', value );
	}

	async updateState( directive: string ): Promise<void> {
		if ( this.disabled ) {
			return;
		}

		switch ( this.state ) {
			case 'blocked':
				switch ( directive ) {
					case 'unblock':
						this.unblockGroup();
						this.renderContents();
						this.state = 'unblocked';

						// Set focus
						// https://www.w3.org/WAI/ARIA/apg/patterns/button/
						// If the button action indicates a context change, such as move to next step in a wizard or add another search criteria,
						// then it is often appropriate to move focus to the starting point for that action.
						const focusableElement = this.firstFocusableElement();
						if ( focusableElement ) {

							// We expect the blocked content to often be iframes,
							// so we add special provisions to move the focus correctly.
							if ( focusableElement instanceof HTMLIFrameElement ) {
								let abortFocusMove = false;

								window.addEventListener( 'click', () => {
									abortFocusMove = true;
								}, {
									capture: true,
									once: true,
								} );

								window.addEventListener( 'blur', () => {
									abortFocusMove = true;
								}, {
									capture: true,
									once: true,
								} );

								window.addEventListener( 'focus', () => {
									abortFocusMove = true;
								}, {
									capture: true,
									once: true,
								} );

								focusableElement.addEventListener( 'load', () => {
									if ( abortFocusMove ) {
										return;
									}

									focusableElement.contentWindow?.focus();
								}, {
									once: true,
								} );
							} else {
								window.requestAnimationFrame( () => {
									focusableElement.focus( {
										preventScroll: true,
									} );
								} );
							}
						}

						break;
					default:
				}
				break;
			default:
		}
	}

	connectedCallback() {
		// Default State
		if ( !this.state ) {
			this.state = 'blocked';
		}

		window.requestAnimationFrame( () => {
			if ( this.checkGroupIsUnblocked() ) {
				this.renderContents();
				this.state = 'unblocked';
			}
		} );
	}

	private renderContents() {
		const contentStr = this.getAttribute( 'data-content' );
		if ( !contentStr ) {
			return;
		}

		this.removeAttribute( 'data-content' );

		const content = JSON.parse( contentStr );
		if ( !content || !content.html ) {
			return;
		}

		this.innerHTML = content.html;
	}

	private unblockGroup() {
		if ( this.disabled ) {
			return;
		}

		const group = this.getAttribute( 'data-group' );
		if ( !group ) {
			return;
		}

		try {
			const alreadyUnblocked: Set<string> = new Set( JSON.parse( window.sessionStorage.getItem( SESSION_STORAGE_KEY ) ?? '[]' ) );
			alreadyUnblocked.add( group );

			window.sessionStorage.setItem( SESSION_STORAGE_KEY, JSON.stringify( Array.from( alreadyUnblocked ) ) );
		} catch ( error ) {
			console.warn( error );
		}
	}

	private checkGroupIsUnblocked() {
		const group = this.getAttribute( 'data-group' );
		if ( !group ) {
			return false;
		}

		try {
			const alreadyUnblocked: Set<string> = new Set( JSON.parse( window.sessionStorage.getItem( SESSION_STORAGE_KEY ) ?? '[]' ) );

			if ( alreadyUnblocked.has( group ) ) {
				return true;
			}
		} catch ( error ) {
			console.warn( error );

			return false;
		}

		return false;
	}

	private firstFocusableElement(): HTMLElement | void {
		const firstFocusableChild = this.querySelector( 'iframe, [tabindex]' );
		if ( firstFocusableChild && firstFocusableChild instanceof HTMLElement ) {
			return firstFocusableChild;
		}

		return;
	}
}
